<template>
    <section>
        <div class="w-full shadow-xl bg-white rounded-t-md" v-if="fileLink">
            <div class="h-36 w-36 rounded pt-2 pl-2">
                <div class="h-full w-full relative" v-tooltip="file.name">
                    <img
                        class="object-cover h-full w-full block cursor-pointer rounded"
                        :src="fileLink"
                        v-img
                        v-if="file.type != 'application/pdf'"
                    >
                    <a :href="fileLink" target="_blank" v-else>
                        <nio-icon class="object-cover h-full w-full p-6 rounded bg-gray-100 shadow text-gray-500" name="light/office-file-pdf" />
                    </a>
                    <nio-button
                        class="z-10 w-6 h-6 bg-gray-200 absolute delete-button rounded-full | transition duration-300 hover:text-red-500"
                        :icon-only="true"
                        @click="deleteFile"
                    >
                        <nio-icon name="regular/bin-2-alternate" />
                    </nio-button>
                </div>
            </div>
        </div>
        <div class="flex items-end space-x-2 px-2 shadow-xl bg-white" :class="fileLink ? 'rounded-b-md pb-2' : 'rounded-md py-2'">
            <textarea
                class="flex-1 p-2"
                :placeholder="$t('chat.messageInput.enterMessage')"
                :readonly="sending"
                rows="1"
                ref="textarea"
                @keyup.enter="enterPressed"
                @mousedown.stop
                v-model="messageBody"
            />

            <div class="inline-flex space-x-2">
                <message-segments-usage
                    class="hidden rounded | md:inline-flex"
                    :file="!!file"
                    :message="messageBody"
                />

                <div class="flex space-x-1">
                    <nio-button
                        :disabled="!!file"
                        :icon-only="true"
                        :loading="uploadingFile"
                        @click="triggerLoadFile"
                        v-tooltip="$t('chat.messageInput.uploadFile')"
                        v-if="supportsMms"
                    >
                        <input
                            accept="application/pdf, image/gif, image/jpeg, image/png"
                            class="hidden"
                            type="file"
                            ref="fileInput"
                            @change="loadFile"
                        >
                        <nio-icon class="text-lg" name="regular/common-file-horizontal-image" />
                    </nio-button>
                    <nio-button
                        :icon-only="true"
                        @click="openTemplatesModal"
                        v-tooltip="$t('chat.messageInput.insertTemplate')"
                    >
                        <nio-icon class="text-lg" name="regular/common-file-text" />
                    </nio-button>
                    <nio-emoji-picker @select="addEmoji" v-if="!$configs.isMobile">
                        <template #trigger>
                            <nio-button class="h-full" :icon-only="true" v-tooltip="$t('chat.messageInput.addEmoji')">
                                <nio-icon class="text-lg" name="regular/smiley-smile-2" />
                            </nio-button>
                        </template>
                    </nio-emoji-picker>
                </div>
                <div v-tooltip="conversationProviderPhonesUnavailableTooltip()">
                    <nio-button
                        color="blue"
                        :disabled="conversationProviderPhonesUnavailable || uploadingFile || messageBodyIsEmpty && !file"
                        :icon-only="!['xl', '2xl'].includes($mq)"
                        :loading="sending"
                        @click="send"
                    >
                        <nio-icon class="text-base xl:mr-2" name="regular/send-email" />
                        <span class="hidden xl:inline">{{ $t('global.general.send') }}</span>
                    </nio-button>
                </div>
            </div>
        </div>

        <conversation-templates-modal @insert="insertTemplate" />
        <payment-modal />
    </section>
</template>

<script>
    import { mapState, mapGetters, mapMutations } from 'vuex';
    import autosize from 'autosize';
    import Vapor from 'laravel-vapor';

    import { unicodeToShortname } from '@/utils/emoji.js';
    import insertAtCaret from 'insert-text-at-cursor';
    import MessageParsing from '@/mixins/MessageParsing.js';
    import ConversationTemplatesModal from '@/components/Modals/ConversationTemplatesModal.vue';
    import MessageSegmentsUsage from '@/components/MessageSegmentsUsage.vue';
    import PaymentModal from '@/components/Modals/PaymentModal.vue';

    export default {
        components: {
            ConversationTemplatesModal,
            PaymentModal,
            MessageSegmentsUsage,
        },

        mixins: [MessageParsing],

        props: {
            conversation: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                messageBody: '',
                uploadingFile: false,
                file: null,
                fileLink: null,
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('chat', [
                'sending',
                'templateCategories',
            ]),

            ...mapGetters('chat', [
                'selectedConversation',
            ]),

            supportsMms() {
                return this.conversation.provider_phone.provider == 'twilio';
            },

            messageBodyWithShortname() {
                return unicodeToShortname(this.messageBody);
            },

            messageBodyIsEmpty() {
                return !this.messageBody.trim();
            },

            conversationProviderPhonesUnavailable() {
                return !this.conversation.provider_phone.active;
            },
        },

        watch: {
            conversation() {
                this.getMessageFromStorage();

                this.$nextTick(() => {
                    if (this.$refs.textarea) {
                        autosize.update(this.$refs.textarea);
                    }
                });
            },

            messageBody() {
                if (!this.messageBodyIsEmpty) {
                    localStorage[`conversation_${this.conversation.id}_message`] = this.messageBodyWithShortname;
                } else {
                    localStorage.removeItem(`conversation_${this.conversation.id}_message`);
                }
            },
        },

        methods: {
            ...mapMutations('chat', [
                'addMessage',
                'markAllMessagesAsRead',
                'setSending',
            ]),

            addEmoji(emoji) {
                this.insertText(unicodeToShortname(emoji));
            },

            insertText(text) {
                insertAtCaret(this.$refs.textarea, text);
            },

            openTemplatesModal() {
                this.$modal.open('conversationTemplates');
            },

            openInvoiceModal() {
                this.$modal.open('invoice', {
                    contact: this.selectedConversation.contact_phone.contact,
                });
            },
            triggerLoadFile() {
                this.$refs.fileInput.click();
            },

            loadFile(event) {
                this.file = event.target.files[0];
                this.fileLink = URL.createObjectURL(this.file);
                this.$refs.fileInput.value = '';
            },

            deleteFile() {
                this.file = null;
                this.fileLink = null;
            },

            async uploadFile() {
                const file = this.file;

                if (!this.supportsMms || !file) {
                    return;
                }

                this.uploadingFile = true;

                try {
                    const s3Response = await Vapor.store(file, {
                        bucket: process.env.VUE_APP_S3_MEDIA_BUCKET,
                        baseURL: this.$axios.defaults.baseURL,
                        headers: this.$axios.defaults.headers.common,
                    });

                    const response = await this.$axios.post('messages/attachment', {
                        uuid: s3Response.uuid,
                        key: s3Response.key,
                        bucket: s3Response.bucket,
                        name: file.name,
                        content_type: file.type,
                        conversation_id: this.conversation.id,
                    });

                    this.addMessage({ message: response.data.data });
                } catch (error) {
                    if (error.response && error.response.status == 422 && !empty(error.response.data.errors.file)) {
                        this.$notify.show({
                            type: 'error',
                            message: error.response.data.errors.file[0],
                        });
                    } else {
                        this.$notify.show({
                            type: 'error',
                            message: this.$t('global.errors.unknown'),
                        });

                        throw error;
                    }
                } finally {
                    this.uploadingFile = false;
                    this.fileLink = null;
                    this.file = null;
                }
            },

            enterPressed(event) {
                if (event.altKey && this.authUser.options.quick_send) {
                    event.preventDefault();

                    if (!this.messageBodyIsEmpty) {
                        this.send();
                    }
                }
            },

            async send() {
                if (this.messageBodyIsEmpty && !this.file) {
                    return;
                }

                if (this.file) {
                    await this.uploadFile();
                }

                this.sendMessage();
            },

            async sendMessage() {
                if (this.messageBodyIsEmpty) {
                    return;
                }

                this.setSending(true);

                this.markAllMessagesAsRead(this.conversation.id);

                try {
                    const response = await this.$axios.post('messages', {
                        body: this.messageBodyWithShortname,
                        conversation_id: this.conversation.id,
                    });

                    this.addMessage({ message: response.data.data });
                    this.messageBody = '';
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: error.response && error.response.status === 422 ? error.response.data.message : this.$t('chat.messageInput.sendingFailed'),
                    });
                } finally {
                    this.setSending(false);
                }

                this.$nextTick(() => {
                    if (this.$refs.textarea) {
                        autosize.update(this.$refs.textarea);
                    }
                });
            },

            getMessageFromStorage() {
                const storedMessage = localStorage[`conversation_${this.conversation.id}_message`];

                this.messageBody = storedMessage || '';
            },

            insertTemplate(template) {
                let message = template.content[this.selectedConversation.contact_phone.contact.locale];
                message = this.fillTemplateValues(message, this.selectedConversation, null, false);

                if (message) {
                    this.insertText(message);
                }
            },

            conversationProviderPhonesUnavailableTooltip() {
                return this.conversationProviderPhonesUnavailable ? this.$t('chat.messageInput.providerPhoneUnavailableTooltip') : '';
            },
        },

        async mounted() {
            this.getMessageFromStorage();

            await this.$nextTick();

            if (this.$refs.textarea) {
                autosize(this.$refs.textarea);

                if (this.$configs.inIframe) {
                    this.$refs.textarea.focus();
                }
            }
        },

        beforeDestroy() {
            if (this.$refs.textarea) {
                autosize.destroy(this.$refs.textarea);
            }
        },
    };
</script>

<style>
.delete-button {
    top: -6px;
    right: -4px;
}
</style>
